import { InventoryPool, InventoryPosition, Vault, WeiPerEther } from 'nftx.js';
import { Control, Controller } from 'react-hook-form';
import { Values } from '../types';
import { ReactNode, useMemo } from 'react';
import { useFormatter } from 'use-intl';
import Settings from '@/presentation/components/widgets/Settings';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import FormattedEther from '@/presentation/components/elements/FormattedEther';
import Collapsible from '@/presentation/components/modules/Collapsible';
import { parseEther } from 'viem';
import Price from '@/presentation/components/elements/Price';
import ActionButton from '@/presentation/components/elements/ActionButton';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import Alert from '@/presentation/components/ui/Alert';
import {
  PercentField,
  WithdrawAssetsCollapse,
  ZeroNftsAlert,
} from '@/presentation/components/modules/Withdraw';

// TODO: intl

const WithdrawInventoryReady = ({
  assets,
  control,
  exitFee,
  premiumFee,
  vaultFee,
  itemCount,
  onSubmit,
  vault: {
    vaultId,
    token: { symbol },
  },
  position: { vToken, claimableRewards, lockedUntil, vTokenLockedUntil },
  vTokenToWithdraw,
}: {
  position: InventoryPosition;
  vault: Vault;
  control: Control<Values>;
  vTokenToWithdraw: bigint;
  onSubmit: (e: any) => void;
  assets: ReactNode | undefined;
  exitFee: bigint;
  premiumFee: bigint;
  vaultFee: bigint;
  itemCount: number;
}) => {
  const now = Math.floor(Date.now() / 1000);
  const { dateTime } = useFormatter();
  const lockState = useMemo(() => {
    if (vTokenLockedUntil > now) {
      return 'vTokenLocked';
    }
    if (lockedUntil > now) {
      return 'locked';
    }
    return 'unlocked';
  }, [lockedUntil, now, vTokenLockedUntil]);

  const canWithdrawNfts = vTokenToWithdraw >= WeiPerEther;

  return (
    <form className="p-4 space-y-4" onSubmit={onSubmit}>
      <div className="flex justify-between relative">
        <h1 className="font-bold text-lg">Remove Inventory</h1>
        <Settings />
      </div>
      <div className="flex justify-between">
        <div className="flex items-center">
          <VaultIcon vaultId={vaultId} className="mr-2 h-6 w-6" />
          <div className="font-medium">{symbol}</div>
        </div>
        <div>
          <h2 className="font-medium">{'Balance'}</h2>
          <div className="text-sm">
            <FormattedEther
              value={vToken + claimableRewards}
              maximumFractionDigits={2}
            />{' '}
            {symbol}
          </div>
        </div>
      </div>

      <PercentField control={control} />

      <WithdrawAssetsCollapse
        enabled={canWithdrawNfts}
        assets={assets}
        selectedCount={itemCount}
      />

      <div className="bg-gray-100 border border-gray-300 rounded flex justify-between items-center p-4">
        <h2 className="text-lg font-medium flex-1">You receive</h2>
        <div className="font-medium grid grid-cols-[24px_auto] gap-2 items-center">
          {canWithdrawNfts && (
            <>
              <VaultIcon vaultId={vaultId} className="h-5 w-5" />
              <div className="flex space-x-2">
                <span>{itemCount}</span>
                <span>{'NFTs'}</span>
              </div>
            </>
          )}
          <VaultIcon vaultId={vaultId} className="h-5 w-5" />
          <div className="flex space-x-2">
            <span>
              <FormattedEther
                value={vTokenToWithdraw - parseEther(`${itemCount}`)}
              />
            </span>
            <span>{symbol}</span>
          </div>
          {claimableRewards > 0 && (
            <>
              <Icon name={Icons.ETH} className="mr-4" size={20} />
              <div className="flex space-x-2">
                <span>
                  <FormattedEther value={claimableRewards} />
                </span>
                <span>{'ETH'}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {lockState === 'vTokenLocked' && (
        <Alert>
          {`This position is locked until ${dateTime(vTokenLockedUntil * 1000, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}. You cannot withdraw from this position until the initial lock has ended.`}
        </Alert>
      )}
      {lockState === 'locked' && (
        <Alert>
          {`This position is locked until ${dateTime(lockedUntil * 1000, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}. If you withdraw any inventory before this time you will need to pay an exit fee.`}
        </Alert>
      )}
      {canWithdrawNfts && <ZeroNftsAlert count={itemCount} />}

      {(vaultFee > 0 || premiumFee > 0 || exitFee > 0) && (
        <div className="bg-gray-100 border border-gray-300 rounded flex justify-between items-center p-4 uppercase">
          {vaultFee > 0 && (
            <>
              <h2 className="font-bold text-xs">{'Fee - Redeem'}</h2>
              <div className="ml-auto font-medium text-sm">
                <Price currency="ETH" value={vaultFee} />
              </div>
            </>
          )}
          {premiumFee > 0 && (
            <>
              <h3 className="font-bold text-xs">{'Fee - Premium'}</h3>
              <div className="ml-auto font-medium text-sm">
                <Price currency="ETH" value={premiumFee} />
              </div>
            </>
          )}
          {exitFee > 0 && (
            <>
              <h3 className="font-bold text-xs">{'Fee - Early withdrawal'}</h3>
              <div className="ml-auto font-medium text-sm">
                <FormattedEther value={exitFee} /> {symbol}
              </div>
            </>
          )}
        </div>
      )}

      <div>
        <ActionButton
          blendColor="after:bg-gray-50"
          type="submit"
          className="w-full"
          disabled={lockState === 'vTokenLocked'}
        >
          Withdraw
        </ActionButton>
      </div>
    </form>
  );
};

export default WithdrawInventoryReady;
